import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useI18n } from "../lib/i18n.jsx";
import {
  Sun,
  Lock,
  Sparkles,
  Mail,
  ArrowUpRight,
  CalendarDays,
  ShieldCheck,
  Flame,
} from "lucide-react";

/**
 * Ultrasecret Tarot — commercially framed teaser page.
 * Dates last synced from https://www.joostvanderleij.nl/agenda on 2026-07-10.
 * When the source agenda updates, refresh the arrays below (they're intentionally
 * co-located so a single file edit is the whole maintenance job).
 */

const ABC_PRACTITIONER = {
  href: "https://www.abcnlp.nl/abc-nlp-practitioner/",
  dates_2026: ["24 sep", "22 okt", "19 nov", "17 dec"],
  dates_2027: ["28 jan", "25 feb", "25 mrt", "22 apr", "27 mei", "25 · 26 · 27 jun"],
};

const ABC_MASTER = {
  href: "https://www.abcnlp.nl/abc-nlp-master-practitioner/",
  dates_2026: ["25 sep", "23 okt", "20 nov", "18 dec"],
  dates_2027: ["29 jan", "26 feb", "26 mrt", "23 apr", "28 mei", "11 · 12 · 13 jun"],
};

const NLP_MAGICK_URL = "https://www.nlpmagick.net/nlp-magick-for-nlp-master-practitioners/";
const CONTACT_EMAIL = "joost@tiouw.com";

export default function TarotUltrasecretPage() {
  const { t } = useI18n();

  // Pull the next NLP Magick event straight from the app's own events feed
  // (this is the one shown in the footer and on /admin), so admin edits there
  // update this page automatically. Falls back gracefully if none present.
  const [nlpMagickEvent, setNlpMagickEvent] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/events");
        if (cancelled) return;
        const match = (data || []).find((e) => /magick|magiër|magier/i.test(e.name));
        setNlpMagickEvent(match || null);
      } catch (_) {
        if (!cancelled) setNlpMagickEvent(null);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <main data-testid="tarot-ultrasecret-page" className="max-w-5xl mx-auto px-6 md:px-10 py-12 md:py-16">
      {/* -------- Hero -------- */}
      <header className="mb-12">
        <p className="label-small text-[#8B3A3A] mb-3 flex items-center gap-2">
          <Sun size={12} /> {t("tarot.ultrasecret_eyebrow")}
        </p>
        <h1 className="font-display text-5xl sm:text-6xl tracking-tight text-[#1A1918]">
          {t("tarot.ultrasecret_heading")}
        </h1>
      </header>

      {/* -------- The secret + veil -------- */}
      <section className="grid grid-cols-1 md:grid-cols-5 gap-8 md:gap-12 mb-16" data-testid="ultrasecret-veil">
        <div className="md:col-span-3">
          <p className="text-[17px] leading-[1.8] text-[#1A1918] mb-5">
            {t("ultrasecret.teaser_1")}
          </p>
          <p className="text-[17px] leading-[1.8] text-[#4A4238] mb-5 italic">
            {t("ultrasecret.teaser_2")}
          </p>
          <p className="text-[17px] leading-[1.8] text-[#1A1918]">
            {t("ultrasecret.teaser_3")}
          </p>
        </div>
        <aside className="md:col-span-2">
          <div className="border border-[#8B3A3A]/60 bg-[#EAE0CA]/50 p-8 h-full flex flex-col justify-center relative overflow-hidden">
            <Lock size={20} className="text-[#8B3A3A] mb-4" />
            <p className="label-small text-[#8B3A3A] mb-3">{t("ultrasecret.gate_label")}</p>
            <p className="font-display italic text-2xl text-[#1A1918] leading-snug">
              {t("ultrasecret.gate_title")}
            </p>
            <p className="text-sm text-[#4A4238] mt-3 leading-relaxed">
              {t("ultrasecret.gate_body")}
            </p>
          </div>
        </aside>
      </section>

      {/* -------- NLP Magick training -------- */}
      <section className="border border-[#C1B49A] bg-[#EAE0CA]/40 p-8 md:p-10 mb-16" data-testid="ultrasecret-training">
        <p className="label-small text-[#8B3A3A] mb-4 flex items-center gap-2">
          <Flame size={12} /> {t("ultrasecret.training_eyebrow")}
        </p>
        <h2 className="font-display text-3xl md:text-4xl text-[#1A1918] mb-4">
          {t("ultrasecret.training_heading")}
        </h2>

        {nlpMagickEvent ? (
          <div className="border-l-2 border-[#8B3A3A] pl-5 py-2 mb-6" data-testid="ultrasecret-next-date">
            <p className="label-small text-[#8B3A3A] mb-1 flex items-center gap-2">
              <CalendarDays size={12} /> {t("ultrasecret.next_date_label")}
            </p>
            <p className="font-display text-3xl italic text-[#1A1918]">{nlpMagickEvent.name}</p>
            <p className="font-mono text-xs tracking-[0.22em] uppercase text-[#8B3A3A] font-semibold mt-1">
              {nlpMagickEvent.date_label}
            </p>
          </div>
        ) : (
          <p className="italic text-sm text-[#7A7061] mb-6">{t("ultrasecret.no_date")}</p>
        )}

        <p className="text-[16px] leading-[1.75] text-[#1A1918] mb-4">
          {t("ultrasecret.only_for_masters_a")}{" "}
          <strong>{t("ultrasecret.only_for_masters_bold")}</strong>{" "}
          {t("ultrasecret.only_for_masters_b")}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-[#C1B49A] mt-6">
          <div className="p-6 bg-[#F4EEDF] border-b md:border-b-0 md:border-r border-[#C1B49A]">
            <ShieldCheck size={18} className="text-[#8B3A3A] mb-3" />
            <p className="label-small text-[#8B3A3A] mb-2">{t("ultrasecret.path_a_label")}</p>
            <h3 className="font-display text-xl text-[#1A1918] mb-2">{t("ultrasecret.path_a_title")}</h3>
            <p className="text-sm text-[#4A4238] leading-relaxed mb-4">
              {t("ultrasecret.path_a_body")}
            </p>
            <a
              href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent("NLP Magick — aanmelding")}`}
              data-testid="ultrasecret-mailto"
              className="btn-primary inline-flex items-center gap-2"
            >
              <Mail size={12} /> {t("ultrasecret.mail_cta")}
            </a>
            <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#7A7061] mt-3">
              {CONTACT_EMAIL}
            </p>
          </div>

          <div className="p-6 bg-[#F4EEDF]">
            <Sparkles size={18} className="text-[#8B3A3A] mb-3" />
            <p className="label-small text-[#8B3A3A] mb-2">{t("ultrasecret.path_b_label")}</p>
            <h3 className="font-display text-xl text-[#1A1918] mb-2">{t("ultrasecret.path_b_title")}</h3>
            <p className="text-sm text-[#4A4238] leading-relaxed mb-4">
              {t("ultrasecret.path_b_body")}
            </p>
            <a
              href={ABC_PRACTITIONER.href}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="ultrasecret-abc-cta"
              className="btn-primary inline-flex items-center gap-2"
            >
              {t("ultrasecret.abc_cta")} <ArrowUpRight size={12} />
            </a>
          </div>
        </div>
      </section>

      {/* -------- Commercial funnel: ABC-NLP -------- */}
      <section className="mb-16" data-testid="ultrasecret-abc-funnel">
        <p className="label-small text-[#8B3A3A] mb-4">{t("ultrasecret.funnel_eyebrow")}</p>
        <h2 className="font-display text-4xl md:text-5xl text-[#1A1918] tracking-tight mb-6">
          {t("ultrasecret.funnel_heading")}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
          <p className="text-[16px] leading-[1.85] text-[#1A1918]">
            {t("ultrasecret.funnel_body_1")}
          </p>
          <p className="text-[16px] leading-[1.85] text-[#4A4238] italic">
            {t("ultrasecret.funnel_body_2")}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 border border-[#C1B49A]">
          <TrainingBlock
            eyebrow={t("ultrasecret.abc_p_eyebrow")}
            title={t("ultrasecret.abc_p_title")}
            body={t("ultrasecret.abc_p_body")}
            dates={ABC_PRACTITIONER}
            href={ABC_PRACTITIONER.href}
            cta={t("ultrasecret.abc_p_cta")}
            testId="ultrasecret-abc-practitioner"
            highlight
          />
          <TrainingBlock
            eyebrow={t("ultrasecret.abc_m_eyebrow")}
            title={t("ultrasecret.abc_m_title")}
            body={t("ultrasecret.abc_m_body")}
            dates={ABC_MASTER}
            href={ABC_MASTER.href}
            cta={t("ultrasecret.abc_m_cta")}
            testId="ultrasecret-abc-master"
          />
        </div>

        <p className="text-xs italic text-[#7A7061] mt-4 text-right">
          {t("ultrasecret.source_note")}{" "}
          <a href="https://www.joostvanderleij.nl/agenda" target="_blank" rel="noopener noreferrer" className="underline underline-offset-4">
            joostvanderleij.nl/agenda
          </a>
        </p>
      </section>

      {/* -------- Final coda -------- */}
      <section className="border-t border-[#C1B49A] pt-12">
        <div className="max-w-3xl">
          <p className="font-display italic text-3xl md:text-4xl text-[#1A1918] leading-snug mb-6">
            {t("ultrasecret.coda")}
          </p>
          <div className="flex flex-wrap gap-4">
            <a
              href={ABC_PRACTITIONER.href}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="ultrasecret-final-cta"
              className="btn-primary inline-flex items-center gap-2"
            >
              {t("ultrasecret.final_cta")} <ArrowUpRight size={14} />
            </a>
            <a href={NLP_MAGICK_URL} target="_blank" rel="noopener noreferrer" className="btn-ghost inline-flex items-center gap-2" data-testid="ultrasecret-magick-info">
              {t("ultrasecret.magick_info_cta")} <ArrowUpRight size={14} />
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}

function TrainingBlock({ eyebrow, title, body, dates, href, cta, testId, highlight }) {
  const { t } = useI18n();
  return (
    <article
      data-testid={testId}
      className={`p-8 md:p-10 ${highlight ? "bg-[#F4EEDF]" : "bg-[#EAE0CA]/40"} ${highlight ? "lg:border-r border-[#C1B49A]" : "border-t lg:border-t-0 border-[#C1B49A]"}`}
    >
      <p className="label-small text-[#8B3A3A] mb-3">{eyebrow}</p>
      <h3 className="font-display text-3xl text-[#1A1918] mb-4 leading-tight">{title}</h3>
      <p className="text-sm text-[#4A4238] leading-relaxed mb-6">{body}</p>

      <div className="mb-6">
        <p className="label-small mb-2">{t("ultrasecret.dates_2026")}</p>
        <ul className="flex flex-wrap gap-x-4 gap-y-1 font-mono text-xs uppercase tracking-[0.18em] text-[#1A1918]">
          {dates.dates_2026.map((d) => (
            <li key={d}>{d}</li>
          ))}
        </ul>
        <p className="label-small mt-4 mb-2">{t("ultrasecret.dates_2027")}</p>
        <ul className="flex flex-wrap gap-x-4 gap-y-1 font-mono text-xs uppercase tracking-[0.18em] text-[#1A1918]">
          {dates.dates_2027.map((d) => (
            <li key={d}>{d}</li>
          ))}
        </ul>
      </div>

      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="btn-primary inline-flex items-center gap-2"
      >
        {cta} <ArrowUpRight size={12} />
      </a>
    </article>
  );
}
